/**
 * Working days utility for absence requests.
 * Counts business days (Mon–Fri) excluding weekends and holidays.
 */

/**
 * Count working days between two dates (inclusive).
 * Excludes weekends (Sat/Sun) and provided holiday date strings.
 */
export function countWorkingDays(
  dateDebut: string,
  dateFin: string,
  holidays: string[] = [],
): number {
  const start = new Date(dateDebut);
  const end = new Date(dateFin);
  let count = 0;
  const cursor = new Date(start);

  while (cursor <= end) {
    const dow = cursor.getDay(); // 0=Sun, 6=Sat
    if (dow !== 0 && dow !== 6) {
      const y = cursor.getFullYear();
      const m = String(cursor.getMonth() + 1).padStart(2, "0");
      const d = String(cursor.getDate()).padStart(2, "0");
      const key = `${y}-${m}-${d}`;
      if (!holidays.includes(key)) count++;
    }
    cursor.setDate(cursor.getDate() + 1);
  }

  return count;
}

/**
 * Shape minimal nécessaire à l'affichage d'une durée d'absence.
 */
export interface DureeAbsence {
  dateDebut: string;
  dateFin: string;
  nombreJours: number;
  /** false quand la période est bornée par des heures (demi-journée). */
  isJourneeComplete?: boolean | null;
}

const MS_PAR_HEURE = 3_600_000;

/**
 * Durée en heures d'une journée partielle, ou null si la période couvre autre
 * chose qu'une demi-journée le même jour (jour plein, plage multi-jours, ...).
 */
function heuresPartielles({ dateDebut, dateFin, isJourneeComplete }: DureeAbsence): number | null {
  if (isJourneeComplete !== false) return null;

  const debut = new Date(dateDebut);
  const fin = new Date(dateFin);
  if (debut.toDateString() !== fin.toDateString()) return null;

  const heures = (fin.getTime() - debut.getTime()) / MS_PAR_HEURE;
  return heures > 0 ? heures : null;
}

function formatHeures(heures: number): string {
  return `${Number.isInteger(heures) ? heures : heures.toFixed(1)}h`;
}

/**
 * Formate la durée d'une absence : "4h" pour une journée partielle le même jour,
 * "N jour(s)" sinon.
 *
 * `nombreJours` vaut 0.5 pour une demi-journée (voir DemandeDureeService côté
 * backend) : le suffixe "jour(s)" y serait trompeur, d'où le passage en heures.
 */
export function formatDureeAbsence(absence: DureeAbsence): string {
  const heures = heuresPartielles(absence);
  if (heures !== null) return formatHeures(heures);

  const { nombreJours } = absence;
  return `${nombreJours} ${nombreJours > 1 ? "jours" : "jour"}`;
}

/**
 * Variante courte pour les pastilles de calendrier : "4h" ou "3j".
 */
export function formatDureeAbsenceCourte(absence: DureeAbsence): string {
  const heures = heuresPartielles(absence);
  if (heures !== null) return formatHeures(heures);

  return `${absence.nombreJours}j`;
}
